"use client"

import * as React from "react"
import {
  addDays,
  format,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  isSameDay,
  subMonths,
  addMonths,
  getYear,
  setYear,
  getMonth,
  setMonth,
  startOfToday,
  isToday,
  isSameMonth,
  eachDayOfInterval,
  isWithinInterval,
  endOfMonth as getEndOfMonth,
  startOfYear,
  endOfYear,
} from "date-fns"
import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react"
import type { DateRange } from "react-day-picker"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

type DateRangePickerProps = {
  date: DateRange | undefined
  onDateChange: (date: DateRange | undefined) => void
  className?: string
  align?: "start" | "center" | "end"
  position?: "top" | "bottom" | "left" | "right"
  disabled?: boolean
}

export function DateRangePicker({
  date,
  onDateChange,
  className,
  align = "start",
  position = "top",
  disabled = false,
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [month, setCurrentMonth] = React.useState<Date>(date?.from || new Date())
  const [activeTab, setActiveTab] = React.useState<string>("calendar")
  const triggerRef = React.useRef<HTMLButtonElement>(null)

  // Preset date ranges with improved names
  const presets = [
    {
      id: "today",
      name: "Today",
      dateRange: {
        from: startOfDay(new Date()),
        to: endOfDay(new Date()),
      },
    },
    {
      id: "yesterday",
      name: "Yesterday",
      dateRange: {
        from: startOfDay(addDays(new Date(), -1)),
        to: endOfDay(addDays(new Date(), -1)),
      },
    },
    {
      id: "last7days",
      name: "Past 7 days",
      dateRange: {
        from: startOfDay(addDays(new Date(), -6)),
        to: endOfDay(new Date()),
      },
    },
    {
      id: "last30days",
      name: "Past 30 days",
      dateRange: {
        from: startOfDay(addDays(new Date(), -29)),
        to: endOfDay(new Date()),
      },
    },
    {
      id: "thisweek",
      name: "Current week",
      dateRange: {
        from: startOfWeek(new Date(), { weekStartsOn: 1 }),
        to: endOfWeek(new Date(), { weekStartsOn: 1 }),
      },
    },
    {
      id: "lastweek",
      name: "Previous week",
      dateRange: {
        from: startOfWeek(addDays(new Date(), -7), { weekStartsOn: 1 }),
        to: endOfWeek(addDays(new Date(), -7), { weekStartsOn: 1 }),
      },
    },
    {
      id: "thismonth",
      name: "Current month",
      dateRange: {
        from: startOfMonth(new Date()),
        to: endOfMonth(new Date()),
      },
    },
    {
      id: "lastmonth",
      name: "Previous month",
      dateRange: {
        from: startOfMonth(subMonths(new Date(), 1)),
        to: endOfMonth(subMonths(new Date(), 1)),
      },
    },
    {
      id: "thisyear",
      name: "This Year",
      dateRange: {
        from: startOfYear(new Date()),
        to: endOfYear(new Date()),
      },
    },
    {
      id: "lastyear",
      name: "Previous Year",
      dateRange: {
        from: startOfYear(new Date(getYear(new Date()) - 1, 0, 1)),
        to: endOfYear(new Date(getYear(new Date()) - 1, 0, 1)),
      },
    },
  ]

  // Format the date range for display - showing full dates
  const formatDateRange = (range: DateRange | undefined) => {
    if (!range?.from) {
      return "Select date range"
    }

    if (!range.to) {
      return format(range.from, "MMM d, yyyy")
    }

    if (isSameDay(range.from, range.to)) {
      return format(range.from, "MMMM d, yyyy")
    }

    return `${format(range.from, "MMM d, yyyy")} - ${format(range.to, "MMM d, yyyy")}`
  }

  // Handle preset selection
  const handlePresetChange = (preset: (typeof presets)[number]) => {
    onDateChange(preset.dateRange)
    setCurrentMonth(preset.dateRange.from)
    setIsOpen(false)
  }

  // Navigate to previous/next month
  const handlePrevMonth = () => setCurrentMonth(subMonths(month, 1))
  const handleNextMonth = () => setCurrentMonth(addMonths(month, 1))

  // Generate months for the dropdown
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

  // Generate years for the dropdown (5 years back, 5 years forward)
  const currentYear = getYear(new Date())
  const years = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i)

  // Handle month change
  const handleMonthChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newMonth = Number.parseInt(event.target.value)
    setCurrentMonth(setMonth(month, newMonth))
  }

  // Handle year change
  const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newYear = Number.parseInt(event.target.value)
    setCurrentMonth(setYear(month, newYear))
  }

  // Custom compact calendar implementation
  const today = startOfToday()
  const days = eachDayOfInterval({
    start: startOfWeek(startOfMonth(month), { weekStartsOn: 0 }),
    end: endOfWeek(getEndOfMonth(month), { weekStartsOn: 0 }),
  })

  const dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"]

  // Handle date selection
  const handleDateClick = (day: Date) => {
    if (!date?.from) {
      // No start date selected, set it
      onDateChange({ from: day, to: undefined })
      return
    }

    if (date.from && !date.to) {
      // Start date exists but no end date, set end date
      onDateChange({ from: date.from, to: day })
      setTimeout(() => setIsOpen(false), 300)
      return
    }

    // Both dates are selected, start new selection
    onDateChange({ from: day, to: undefined })
  }

  // Check if a date is in the selected range
  const isDateInRange = (day: Date) => {
    if (!date?.from) return false
    if (!date.to) return isSameDay(day, date.from)
    return isWithinInterval(day, { start: date.from, end: date.to })
  }

  // Check if a date is the start of the range
  const isRangeStart = (day: Date) => {
    return date?.from && isSameDay(day, date.from)
  }

  // Check if a date is the end of the range
  const isRangeEnd = (day: Date) => {
    return date?.to && isSameDay(day, date.to)
  }

  return (
    <div className={cn("inline-block", className)}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild disabled={disabled}>
          <Button
            id="date"
            ref={triggerRef}
            disabled={disabled}
            variant={"outline"}
            className={cn(
              "inline-flex whitespace-nowrap min-w-[240px] justify-start",
              !date && "text-muted-foreground",
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
            <span className="truncate">{formatDateRange(date)}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align={align} side={position} sideOffset={4} avoidCollisions={true}>
          <Tabs defaultValue="calendar" value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="calendar" className="text-xs py-1.5">
                Calendar
              </TabsTrigger>
              <TabsTrigger value="presets" className="text-xs py-1.5">
                Quick Select
              </TabsTrigger>
            </TabsList>

            <TabsContent value="calendar" className="p-2">
              {/* Month/Year Navigation - Slightly Larger */}
              <div className="flex items-center justify-between mb-2">
                <Button variant="ghost" size="icon" className="h-6 w-6 p-0" onClick={handlePrevMonth}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>

                <div className="flex items-center gap-1 text-xs">
                  <select
                    value={getMonth(month)}
                    onChange={handleMonthChange}
                    className="h-7 px-1 text-xs border rounded"
                  >
                    {months.map((name, i) => (
                      <option key={name} value={i}>
                        {name}
                      </option>
                    ))}
                  </select>

                  <select
                    value={getYear(month)}
                    onChange={handleYearChange}
                    className="h-7 px-1 text-xs border rounded"
                  >
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>

                <Button variant="ghost" size="icon" className="h-6 w-6 p-0" onClick={handleNextMonth}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>

              {/* Custom Slightly Larger Calendar */}
              <div className="border rounded-md p-1.5">
                <div className="grid grid-cols-7 gap-0 mb-1">
                  {dayNames.map((day) => (
                    <div key={day} className="text-xs text-center font-medium text-muted-foreground">
                      {day}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-0">
                  {days.map((day) => {
                    const isSelected = isDateInRange(day)
                    const isStart = isRangeStart(day)
                    const isEnd = isRangeEnd(day)

                    return (
                      <div
                        key={day.toString()}
                        className={cn(
                          "h-7 w-7 p-0 text-center text-xs relative",
                          !isSameMonth(day, month) && "text-muted-foreground opacity-50",
                          isToday(day) && "font-bold",
                          isSelected && "bg-blue-50",
                          isStart && "bg-blue-100 rounded-md",
                          isEnd && "bg-blue-100 rounded-md",
                        )}
                      >
                        <button
                          type="button"
                          onClick={() => handleDateClick(day)}
                          className={cn(
                            "absolute inset-0 flex items-center justify-center hover:bg-blue-100 transition-colors",
                            isStart || isEnd ? "bg-blue-700 text-white rounded-md" : "",
                            isToday(day) && !isSelected && "border border-blue-400",
                          )}
                        >
                          {format(day, "d")}
                        </button>
                      </div>
                    )
                  })}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="presets" className="p-2">
              <div className="grid grid-cols-2 gap-1.5">
                {presets.map((preset) => (
                  <Button
                    key={preset.id}
                    variant="outline"
                    size="sm"
                    className="justify-start font-normal text-xs h-7 px-2"
                    onClick={() => handlePresetChange(preset)}
                  >
                    {preset.name}
                  </Button>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </PopoverContent>
      </Popover>
    </div>
  )
}
